import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { Store, Save, CheckCircle2 } from 'lucide-react';

interface InnerSettings {
  defaultTaxRate?: number;
  displayName?: string;
  [key: string]: any;
}

export function AdminStore() {
  const { organization, role, updateOrganization } = useAuth();
  const isOwner = role === 'owner';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const [displayName, setDisplayName] = useState('');
  const [taxRate, setTaxRate] = useState('0');
  const [existingSettings, setExistingSettings] = useState<InnerSettings>({});

  useEffect(() => {
    api.getOrganizationSettings()
      .then((data: any) => {
        const settings: InnerSettings = data?.settings || {};
        setExistingSettings(settings);
        if (settings.displayName) setDisplayName(settings.displayName);
        if (typeof settings.defaultTaxRate === 'number') setTaxRate(String(settings.defaultTaxRate));
      })
      .catch((err: any) => {
        if (err?.message) setError(err.message);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!isOwner) return;
    setError('');
    setSaving(true);
    setSavedAt(null);
    try {
      const parsed = parseFloat(taxRate);
      const safeRate = isNaN(parsed) ? 0 : Math.max(0, Math.min(100, parsed));
      const nextSettings: InnerSettings = {
        ...existingSettings,
        displayName: displayName.trim() || undefined,
        defaultTaxRate: safeRate,
      };
      await api.updateOrganizationSettings({ settings: nextSettings });
      setExistingSettings(nextSettings);
      updateOrganization({ displayName: nextSettings.displayName || null });
      setSavedAt(Date.now());
    } catch (err: any) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96 text-slate-400 text-lg">Loading...</div>;

  const taxNum = parseFloat(taxRate) || 0;

  return (
    <div className="max-w-3xl mx-auto pb-24">
      {/* Header */}
      <div className="mb-8 sticky top-0 bg-[#f0f4f8]/80 backdrop-blur-md py-4 z-10 border-b border-transparent">
        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Store Settings</h1>
        <p className="text-slate-500 font-medium mt-1">Configure your store preferences</p>
      </div>

      {error && (
        <div className="px-5 py-4 bg-red-50 text-red-600 border border-red-100 rounded-2xl mb-6 font-medium">
          {error}
        </div>
      )}

      {savedAt && (
        <div className="flex items-center gap-2 px-5 py-4 bg-blue-50 text-[#1e40af] border border-blue-200 rounded-2xl mb-6 font-medium">
          <CheckCircle2 className="w-5 h-5" />
          Settings saved
        </div>
      )}

      {!isOwner && (
        <div className="px-5 py-4 bg-amber-50 text-amber-700 border border-amber-200 rounded-2xl mb-6 font-medium">
          You can view these settings, but only owners can change them.
        </div>
      )}

      {/* Store identity card */}
      <div className="bg-white rounded-[2rem] p-8 mb-6 shadow-sm border border-slate-200/60">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-blue-50 border border-blue-100 rounded-2xl flex items-center justify-center">
            <Store className="w-6 h-6 text-[#2563eb]" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Store Identity</h2>
            <p className="text-sm text-slate-500 mt-0.5">Appears in the POS header and on receipts</p>
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">Workspace Name</label>
            <div className="w-full h-14 px-5 bg-slate-50 border border-slate-200 rounded-2xl text-[16px] text-slate-700 font-semibold flex items-center">
              {organization?.name || '—'}
            </div>
            <p className="text-xs text-slate-400 mt-1.5 ml-1">
              Set when the workspace was created. Contact your platform administrator to rename.
            </p>
          </div>

          <div>
            <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">Display Name (optional)</label>
            <input
              type="text"
              value={displayName}
              disabled={!isOwner}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={organization?.name || 'Enter a custom display name'}
              className="w-full h-14 px-5 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all placeholder-slate-300 disabled:bg-slate-50 disabled:cursor-not-allowed"
            />
            <p className="text-xs text-slate-400 mt-1.5 ml-1">
              Example: "Blue Wave Café" or "Main Street Coffee"
            </p>
          </div>
        </div>
      </div>

      {/* Tax rate card */}
      <div className="bg-white rounded-[2rem] p-8 mb-6 shadow-sm border border-slate-200/60">
        <div className="mb-6">
          <h2 className="text-lg font-bold text-slate-900">Default Sales Tax Rate</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Pre-fills the tax rate when creating new products
          </p>
        </div>

        <div>
          <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">Tax Rate (%)</label>
          <div className="relative max-w-xs">
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={taxRate}
              disabled={!isOwner}
              onChange={(e) => setTaxRate(e.target.value)}
              className="w-full h-14 pl-5 pr-12 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-semibold focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all disabled:bg-slate-50 disabled:cursor-not-allowed"
            />
            <span className="absolute right-5 top-1/2 -translate-y-1/2 text-lg font-semibold text-slate-400">%</span>
          </div>
          <p className="text-sm text-slate-400 mt-2 ml-1">
            Current rate: <span className="font-semibold text-slate-600">{taxNum.toFixed(2)}%</span> &middot; Example: $100 item = ${(100 * (taxNum / 100)).toFixed(2)} tax
          </p>
        </div>

        <div className="mt-6 p-5 bg-blue-50/60 border border-blue-200 rounded-2xl">
          <h4 className="font-semibold text-slate-900 text-sm mb-2">How tax is calculated</h4>
          <ul className="space-y-1.5 text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <span className="text-[#2563eb] mt-0.5">&bull;</span>
              <span>Each product stores its own tax rate, which determines what's charged at checkout</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#2563eb] mt-0.5">&bull;</span>
              <span>The default above pre-fills new product forms so cashiers don't have to retype it</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#2563eb] mt-0.5">&bull;</span>
              <span>Existing products keep their own rates &mdash; edit each product to apply the new default</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Save action */}
      {isOwner && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className={`
              flex items-center gap-2 px-6 py-3.5 rounded-2xl font-bold transition-all active:scale-95 shadow-md
              ${saving
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed shadow-none'
                : 'bg-[#2563eb] text-white hover:bg-[#1d4ed8] shadow-blue-900/10'}
            `}
          >
            <Save className="w-5 h-5" />
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
}
